import React, { useEffect, useState } from 'react';
import { X, Copy, Check, Sprout, DollarSign, Trophy, Star, ChevronRight, ImageDown, Leaf } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function wrapText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawSeasonCard(story) {
  const S = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext('2d');

  const grad = ctx.createLinearGradient(0, 0, S, S);
  grad.addColorStop(0, '#1A4331');
  grad.addColorStop(0.6, '#2E5C46');
  grad.addColorStop(1, '#3E4A2E');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, S, S);

  ctx.strokeStyle = 'rgba(198,107,74,0.5)';
  ctx.lineWidth = 3;
  ctx.strokeRect(40, 40, S - 80, S - 80);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#C9A85B';
  ctx.font = '600 34px Outfit, sans-serif';
  ctx.fillText('H O M E G R O W N', S / 2, 125);

  ctx.fillStyle = '#C66B4A';
  ctx.font = '700 40px Outfit, sans-serif';
  ctx.fillText(`✦ ${story.season_label.toUpperCase()} WRAP ✦`, S / 2, 195);

  ctx.fillStyle = '#FAF6EC';
  ctx.font = '700 58px Outfit, sans-serif';
  ctx.fillText(`${story.user_first_name}'s Season in the Garden`, S / 2, 275);

  ctx.fillStyle = '#C9A85B';
  ctx.font = '700 110px Outfit, sans-serif';
  const valueText = story.value_high > 0 ? `$${Math.round(story.value_low)}–$${Math.round(story.value_high)}` : `${story.crops_sown} crops sown`;
  ctx.fillText(valueText, S / 2, 430);
  ctx.fillStyle = 'rgba(250,246,236,0.85)';
  ctx.font = '500 33px Manrope, sans-serif';
  ctx.fillText(story.value_high > 0 ? 'of homegrown food this season' : 'and a season of groundwork laid', S / 2, 488);

  const stats = [
    [`${story.harvests_count}`, 'harvests'],
    [`${story.total_kg}kg`, 'picked'],
    [`${story.xp_gained}`, 'XP gained'],
  ];
  const boxW = 280, boxH = 160, gap = 30;
  const startX = (S - (boxW * 3 + gap * 2)) / 2;
  stats.forEach(([num, label], i) => {
    const x = startX + i * (boxW + gap);
    ctx.fillStyle = 'rgba(250,246,236,0.10)';
    ctx.beginPath();
    ctx.roundRect(x, 535, boxW, boxH, 28);
    ctx.fill();
    ctx.fillStyle = '#FAF6EC';
    ctx.font = '700 56px Outfit, sans-serif';
    ctx.fillText(num, x + boxW / 2, 615);
    ctx.fillStyle = 'rgba(250,246,236,0.7)';
    ctx.font = '600 25px Manrope, sans-serif';
    ctx.fillText(label.toUpperCase(), x + boxW / 2, 662);
  });

  let y = 770;
  if (story.best_crops?.length > 0) {
    ctx.fillStyle = '#C9A85B';
    ctx.font = '600 32px Manrope, sans-serif';
    const crops = story.best_crops.map((c, i) => `${i + 1}. ${c.crop_name}`).join('   ');
    ctx.fillText(`Best crops:  ${crops}`, S / 2, y);
    y += 55;
  }
  if (story.skills_gained?.length > 0) {
    ctx.fillStyle = 'rgba(250,246,236,0.8)';
    ctx.font = '500 28px Manrope, sans-serif';
    ctx.fillText(`Skills grown: ${story.skills_gained.slice(0, 3).map(s => s.label).join(' · ')}`, S / 2, y);
    y += 55;
  }

  ctx.fillStyle = 'rgba(250,246,236,0.85)';
  ctx.font = 'italic 500 29px Manrope, sans-serif';
  wrapText(ctx, story.share_line, S - 200).slice(0, 2).forEach((l, i) => ctx.fillText(l, S / 2, y + i * 40));

  ctx.fillStyle = 'rgba(250,246,236,0.55)';
  ctx.font = '500 26px Manrope, sans-serif';
  ctx.fillText('Grow your own · homegrown', S / 2, S - 70);
  return canvas;
}

export function SeasonStoryBanner() {
  const [shouldShow, setShouldShow] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/story/season/should-show`, { withCredentials: true })
      .then(r => setShouldShow(r.data.should_show))
      .catch(() => {});
  }, []);

  if (!shouldShow) return null;

  const dismiss = async () => {
    setShouldShow(false);
    setOpen(false);
    try { await axios.post(`${API}/story/season/dismiss`, {}, { withCredentials: true }); } catch (e) { console.error(e); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="season-story-banner"
        className="w-full bg-gradient-to-br from-[#C66B4A] to-[#A34F33] rounded-3xl p-5 flex items-center gap-4 text-left hover:scale-[1.01] transition-transform">
        <div className="w-12 h-12 bg-[#FAF6EC]/20 rounded-full flex items-center justify-center flex-shrink-0">
          <Star className="w-6 h-6 text-[#FAF6EC]" />
        </div>
        <div className="flex-1">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FAF6EC]/80">Season Wrap</p>
          <p className="text-lg font-semibold text-[#FAF6EC] mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            The season is wrapping up — see your big recap
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-[#FAF6EC]/70" />
      </button>
      {open && <SeasonStoryModal onClose={() => setOpen(false)} onDismiss={dismiss} />}
    </>
  );
}

export function SeasonStoryModal({ onClose, onDismiss }) {
  const [story, setStory] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    axios.get(`${API}/story/season`, { withCredentials: true })
      .then(r => setStory(r.data))
      .catch(() => {});
  }, []);

  const copyShare = () => {
    if (!story) return;
    navigator.clipboard.writeText(story.share_line).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  const saveShareImage = async () => {
    if (!story) return;
    try { await document.fonts.ready; } catch (e) { console.error(e); }
    const canvas = drawSeasonCard(story);
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      const file = new File([blob], 'homegrown-season-wrap.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: 'My Season Wrap', text: story.share_line });
          setSaved(true);
          setTimeout(() => setSaved(false), 2500);
          return;
        } catch (e) { console.error(e); }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'homegrown-season-wrap.png';
      a.click();
      URL.revokeObjectURL(url);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }, 'image/png');
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-[#FAF6EC] rounded-3xl max-w-lg w-full max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="season-story-modal">
        {!story ? (
          <div className="p-12 flex justify-center"><div className="w-10 h-10 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div></div>
        ) : (
          <>
            <div className="bg-gradient-to-br from-[#C66B4A] to-[#1A4331] rounded-t-3xl p-6 relative">
              <button onClick={onClose} data-testid="season-story-close" className="absolute top-4 right-4 text-[#FAF6EC]/70 hover:text-[#FAF6EC]"><X className="w-5 h-5" /></button>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#FAF6EC]/80">{story.season_label} · Season Wrap</p>
              <h2 className="text-2xl font-bold text-[#FAF6EC] mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {story.user_first_name}&apos;s Season in the Garden
              </h2>
              <p className="text-sm text-[#FAF6EC]/85 mt-2 leading-relaxed" data-testid="season-story-headline">{story.headline}</p>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#C9A85B]/10 rounded-2xl p-4 text-center col-span-2">
                  <DollarSign className="w-5 h-5 text-[#C9A85B] mx-auto mb-1" />
                  <p className="text-3xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="season-story-value">
                    {story.value_high > 0 ? `$${Math.round(story.value_low)}–$${Math.round(story.value_high)}` : '$0 (yet!)'}
                  </p>
                  <p className="text-[10px] text-[#7A6B57] uppercase tracking-wider mt-1">Food grown this season · {story.total_kg}kg picked</p>
                </div>
                <div className="bg-[#6B9F4A]/10 rounded-2xl p-3 text-center">
                  <Trophy className="w-4 h-4 text-[#6B9F4A] mx-auto mb-1" />
                  <p className="text-lg font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{story.harvests_count}</p>
                  <p className="text-[10px] text-[#7A6B57] uppercase tracking-wider">Harvests</p>
                </div>
                <div className="bg-[#C66B4A]/10 rounded-2xl p-3 text-center">
                  <Sprout className="w-4 h-4 text-[#C66B4A] mx-auto mb-1" />
                  <p className="text-lg font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{story.crops_sown}</p>
                  <p className="text-[10px] text-[#7A6B57] uppercase tracking-wider">Crops sown</p>
                </div>
              </div>

              {story.best_crops.length > 0 && (
                <div data-testid="season-best-crops">
                  <p className="text-xs font-bold tracking-wider uppercase text-[#7A6B57] mb-2">Best crops</p>
                  <div className="space-y-2">
                    {story.best_crops.map((c, i) => (
                      <div key={c.crop_name} className="flex items-center gap-3 bg-white/60 rounded-2xl p-3">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-[#C9A85B] text-white' : 'bg-[#E5EBDD] text-[#7A6B57]'}`}>{i + 1}</span>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-[#1A4331]">{c.crop_name}</p>
                          <p className="text-xs text-[#7A6B57]">{c.total_kg}kg · {c.harvests} harvest{c.harvests !== 1 ? 's' : ''}</p>
                        </div>
                        <p className="text-sm font-bold text-[#1A4331]">${Math.round(c.value_low)}–${Math.round(c.value_high)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {story.skills_gained.length > 0 && (
                <div data-testid="season-skills-gained">
                  <p className="text-xs font-bold tracking-wider uppercase text-[#7A6B57] mb-2">Skills gained · +{story.xp_gained} XP total</p>
                  <div className="flex flex-wrap gap-2">
                    {story.skills_gained.map(s => (
                      <span key={s.key} className="bg-[#6B9F4A]/10 text-[#1A4331] text-xs px-3 py-1.5 rounded-full font-medium">{s.label} +{s.xp} XP</span>
                    ))}
                  </div>
                </div>
              )}

              {story.achievements.length > 0 && (
                <div>
                  <p className="text-xs font-bold tracking-wider uppercase text-[#7A6B57] mb-2">Badges this season</p>
                  <div className="flex flex-wrap gap-2">
                    {story.achievements.map((a, i) => (
                      <span key={i} className="bg-[#C66B4A]/10 text-[#1A4331] text-xs px-3 py-1.5 rounded-full font-medium">{a.icon} {a.title}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-[#E5EBDD] rounded-2xl p-4" data-testid="season-next-preview">
                <p className="text-xs font-bold tracking-wider uppercase text-[#1A4331] mb-2 flex items-center gap-1">
                  <Leaf className="w-3.5 h-3.5 text-[#6B9F4A]" /> Coming up: {story.next_season.name}
                </p>
                {story.next_season.sow_first.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {story.next_season.sow_first.map(c => (
                      <Link key={c.crop_id} to={`/crops/${c.crop_id}`} className="bg-white text-[#1A4331] text-xs px-3 py-1.5 rounded-full font-medium hover:bg-[#6B9F4A]/10">
                        Sow {c.name} →
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-[#7A6B57]">A quieter sowing month — perfect for soil building and planning.</p>
                )}
              </div>

              <div className="space-y-3 pt-1">
                <button onClick={saveShareImage} data-testid="season-story-share-image"
                  className="w-full bg-gradient-to-r from-[#C66B4A] to-[#A34F33] text-[#FAF6EC] font-semibold py-3 rounded-full text-sm flex items-center justify-center gap-2 hover:opacity-90">
                  {saved ? <><Check className="w-4 h-4" /> Season card saved!</> : <><ImageDown className="w-4 h-4" /> Share as picture</>}
                </button>
                <div className="flex gap-3">
                  <button onClick={copyShare} data-testid="season-story-copy"
                    className="flex-1 border-2 border-[#1A4331] text-[#1A4331] font-semibold py-3 rounded-full text-sm flex items-center justify-center gap-2 hover:bg-[#1A4331]/5">
                    {copied ? <><Check className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy share line</>}
                  </button>
                  <button onClick={onDismiss || onClose} data-testid="season-story-dismiss"
                    className="flex-1 bg-[#1A4331] hover:bg-[#255C44] text-[#FAF6EC] font-semibold py-3 rounded-full text-sm">
                    {onDismiss ? 'Done — bring on next season' : 'Close'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
